import { type PlatformTenantDto } from '@dbrain/shared';
import { Alert, Button, Card, Icons, ScrollArea } from '@dbrain/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { api } from '../../api/client.js';
import { keys, useMe } from '../../api/hooks.js';
import { CapacityDaySelect, CapacityPanel, type CapacityDay } from '../../components/admin/Capacity.js';
import { AddProviderDialog, inheritedLabel, modelName, modelUsage, ProviderCard, ProviderList, TaskModels, taskStates } from '../../components/admin/providers.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';
import { PageHeader } from '../../components/PageHeader.js';
import { formatTokens } from '@dbrain/app-core/format';
import { PillTabs } from '../admin/AdminLayout.js';
import { useCapabilities } from '../../experience.js';
import { installationDraft, installationEndpoints, type InstallationView } from '../admin/ModelsAdminPage.js';
import { AuditTab } from './AuditTab.js';
import { BireyselCapacity } from './BireyselCapacity.js';
import { PaymentsCsvButton, PaymentsTab } from './PaymentsTab.js';
import { PlansSaveButton, PlansTab, usePlansEditor } from './PlansTab.js';
import { UsersTab } from './UsersTab.js';

/**
 * The platform operator (SA-069), bireysel only (D-135; PlatformModeller): the platform's providers and models
 * (.env seeds them at the first start; from then on they live here) and the default model per task, which
 * organizations may reach internal provider addresses, the platform's capacity, and the platform audit log.
 * Where the edition has plans (Phase 16a, ADR-0023; B2cPlatform*), people take the organizations' place: Kullanıcılar
 * (Pro by hand, the internal addresses per person), Paketler, and Kapasite's live tiles, model share and fair use.
 */
const TABS = ['providers', 'tenants', 'capacity', 'audit'] as const;
const PLAN_TABS = ['providers', 'users', 'plans', 'capacity', 'audit'] as const;
// Phase 16c (B2cPlatformOdemeler): "Abonelikler ve ödemeler" after Paketler where the edition has billing
const BILLING_TABS = ['providers', 'users', 'plans', 'billing', 'capacity', 'audit'] as const;
type Tab = (typeof TABS)[number] | (typeof PLAN_TABS)[number] | (typeof BILLING_TABS)[number];
const PREFIX = '/api/platform';

function ProvidersTab({ adding, onAdding }: { adding: boolean; onAdding: (open: boolean) => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const view = useQuery({ queryKey: keys.platform, queryFn: () => api<InstallationView>('GET', PREFIX) });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: keys.platform });
    void queryClient.invalidateQueries({ queryKey: keys.models });
  };
  const providers = view.data?.providers ?? [];
  const models = providers.flatMap((p) => p.models.map((m) => ({ ...m, providerName: p.name })));
  const states = taskStates(Object.fromEntries((view.data?.routes ?? []).map((r) => [r.callType, r.modelRef])));
  const usage = modelUsage(states);
  const nameOf = (ref: string) => {
    const m = models.find((x) => x.id === ref);
    return m ? modelName(m) : null;
  };
  // 16c.1 (B2cModelYeni): "Sohbette" with the packages that list each model; what an added provider brought
  const offer = { plans: view.data?.plans ?? {} };
  const offered = models.filter((m) => m.kind === 'chat' && m.chatOffered).length;
  const [added, setAdded] = useState<{ name: string; found: number | null; added: number | null } | null>(null);
  return (
    <>
      {added ? (
        <Alert tone="success" title={added.found ? t('models.offer.added', { name: added.name, found: added.found, added: added.added ?? 0 }) : t('models.offer.addedNone', { name: added.name })} data-testid="provider-added">
          {added.found ? <p>{t('models.offer.addedPlans')}</p> : null}
        </Alert>
      ) : null}
      <ErrorAlert error={view.error} />
      <ProviderList providers={providers.length} models={models.length} offered={offered}>
        {providers.map((p) => (
          <ProviderCard key={p.id} provider={p} models={p.models} endpoints={installationEndpoints(PREFIX, p.id)} onChanged={refresh} usage={usage} scopeLabel={t('models.scopes.platform')} offer={offer} />
        ))}
      </ProviderList>
      <TaskModels
        models={models}
        states={states}
        inherited={inheritedLabel(t, nameOf, null)}
        caption={t('models.routes.caption.platform')}
        testId="platform-routes"
        onSet={async (task, modelRef) => {
          await api('PUT', `${PREFIX}/routes/${task}`, { modelRef });
          refresh();
        }}
      />
      {adding ? (
        <AddProviderDialog
          installation
          onClose={() => onAdding(false)}
          onTest={(draft) => api('POST', `${PREFIX}/providers/test`, { kind: draft.kind, baseUrl: draft.baseUrl ?? '', ...(draft.apiKey ? { apiKey: draft.apiKey } : {}), ...(draft.modelId ? { modelId: draft.modelId } : {}) })}
          onCreate={async (draft) => {
            const created = await api<{ name: string; found: number | null; added: number | null }>('POST', `${PREFIX}/providers`, installationDraft(draft));
            setAdded({ name: created.name, found: created.found, added: created.added });
            refresh();
          }}
        />
      ) : null}
    </>
  );
}

function TenantsTab() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const tenants = useQuery({ queryKey: ['platform', 'tenants'], queryFn: () => api<PlatformTenantDto[]>('GET', `${PREFIX}/tenants`) });
  const toggle = useMutation({
    mutationFn: ({ id, allowed }: { id: string; allowed: boolean }) => api('PUT', `${PREFIX}/tenants/${id}/internal-providers`, { allowed }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
  });
  return (
    <Card title={t('platform.tenants.title')}>
      <p className="muted">{t('platform.tenants.hint')}</p>
      <ErrorAlert error={tenants.error ?? toggle.error} />
      <ScrollArea label={t('platform.tenants.title')} className="table-scroll">
        <table className="context-table" data-testid="platform-tenants">
          <thead>
            <tr>
              <th scope="col">{t('platform.tenants.name')}</th>
              <th scope="col">{t('platform.tenants.members')}</th>
              <th scope="col">{t('platform.tenants.tokens')}</th>
              <th scope="col">{t('platform.tenants.search')}</th>
              <th scope="col">{t('platform.tenants.internal')}</th>
            </tr>
          </thead>
          <tbody>
            {tenants.data?.map((tenant) => (
              <tr key={tenant.id} data-testid={`platform-tenant-${tenant.id}`}>
                <th scope="row">
                  {tenant.kind === 'personal' ? `${tenant.name} (${t('kinds.personal')})` : tenant.name}
                  <br />
                  <span className="muted">{new Date(tenant.createdAt).toLocaleDateString(i18n.language)}</span>
                </th>
                <td>{tenant.members}</td>
                <td>{formatTokens(tenant.tokens30d, i18n.language)}</td>
                <td>{t(`platform.tenants.searchModes.${tenant.documentSearch}`)}</td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={t('platform.tenants.allowInternal', { name: tenant.name })}
                    checked={tenant.allowInternalProviderUrls}
                    disabled={toggle.isPending}
                    onChange={(e) => toggle.mutate({ id: tenant.id, allowed: e.target.checked })}
                    data-testid="internal-toggle"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollArea>
    </Card>
  );
}

export function PlatformPage() {
  const { t } = useTranslation();
  const me = useMe();
  const { plans, billing } = useCapabilities();
  const [chosen, setChosen] = useState<Tab>('providers');
  const [, setSearchParams] = useSearchParams();
  // the lists' filters live in the address (?q=, ?page=…): a tab starts without the one before's
  const setTab = (next: Tab) => {
    setSearchParams(new URLSearchParams(), { replace: true });
    setChosen(next);
  };
  const [adding, setAdding] = useState(false);
  const [day, setDay] = useState<CapacityDay>('today');
  const tabs: readonly Tab[] = plans ? (billing ? BILLING_TABS : PLAN_TABS) : TABS;
  // a tab of the other list (chosen before /api/meta named the edition) falls back to the first
  const tab = tabs.includes(chosen) ? chosen : 'providers';
  // Paketler's fields live here: its save button is in the page header
  const plansEditor = usePlansEditor(plans && me.data?.platformOperator === true && tab === 'plans');
  if (!me.data) return null;
  if (!me.data.platformOperator) {
    return (
      <div className="page">
        <Alert tone="warning" title={t('platform.onlyOperators')} data-testid="platform-only" />
      </div>
    );
  }
  const actions =
    tab === 'providers' ? (
      <Button icon={<Icons.plus size={16} />} onClick={() => setAdding(true)} data-testid="provider-add-open">
        {t('models.add.title')}
      </Button>
    ) : tab === 'plans' ? (
      <PlansSaveButton editor={plansEditor} />
    ) : tab === 'billing' ? (
      <PaymentsCsvButton />
    ) : tab === 'capacity' && !plans ? (
      <CapacityDaySelect value={day} onChange={setDay} />
    ) : undefined;
  const label = (name: Tab) => (name === 'users' || name === 'plans' ? t(`b2cPlatform.tabs.${name}`) : name === 'billing' ? t('b2cBilling.platform.tab') : t(`platform.tabs.${name}`));
  return (
    <div className="page page--wide platform-page">
      <PageHeader eyebrow={t('shell.management')} title={t('platform.title')} intro={plans ? t('b2cPlatform.intro') : t('platform.intro')} actions={actions} />
      <PillTabs label={t('platform.tabsLabel')} value={tab} onChange={setTab} items={tabs.map((name) => ({ id: name, label: label(name), testId: `platform-tab-${name}` }))} />
      <div className="platform-panel">
        {tab === 'providers' ? <ProvidersTab adding={adding} onAdding={setAdding} /> : null}
        {tab === 'tenants' ? <TenantsTab /> : null}
        {tab === 'users' ? <UsersTab /> : null}
        {tab === 'plans' ? <PlansTab editor={plansEditor} /> : null}
        {tab === 'billing' ? <PaymentsTab /> : null}
        {tab === 'capacity' && plans ? <BireyselCapacity /> : null}
        {tab === 'capacity' && !plans ? <CapacityPanel endpoint={`${PREFIX}/capacity`} day={day} /> : null}
        {tab === 'audit' ? <AuditTab /> : null}
      </div>
    </div>
  );
}
